<template>
    <form
        class="simple-search"
        @keydown.up.prevent="prev"
        @keydown.down.prevent="next"
        @keydown.esc.stop="close"
        :action="action"
        method="GET"
        v-click-outside="close"
        role="search"

    >
        <label :for="`simpleSearch-${state.id}`" v-if="label" v-text="label" class="sr-only" />
        <div
            aria-role="combobox"
            :aria-expanded="state.open"
            :aria-owns="state.open ? `simpleSearchResults-${state.id}` : ''"
            aria-haspopup=”listbox”
        >
            <input
                ref="input"
                type="search"
                class="input simple-search__input"
                autocomplete="off"
                aria-autocomplete="list"
                v-model="q"
                @keydown="search"
                @focus="handleFocus"
                :id="`simpleSearch-${state.id}`"
                :placeholder="placeholder ? placeholder : 'Search'"
                :name="name"
                :aria-describedby="`simpleSearchInstructions-${state.id}`"
                :aria-controls="state.open ? `simpleSearchResults-${state.id}` : ''"
                :aria-activedescendant="`simpleSearchSelected-${state.id}`"
            >
            <div class="simple-search__action">
                <button
                    class="icon -search -button"
                    aria-label="Search for Doctor"
                    type="submit"
                    ref="button"
                >
                    <span class="sr-only">Search</span>
                </button>
            </div>
            <div class="simple-search__results-wrap" v-show="state.open">
                <div :class="['simple-search__results-inner', { '-has-bottom-link': type === 'personnel' }]" ref="results">
                    <ul
                        class="simple-search__results"
                        :id="`simpleSearchResults-${state.id}`"
                        tabindex="0"
                        aria-label="Results list"
                        :aria-expanded="results.length ? 'true' : 'false'"
                        aria-role="listbox"
                    >
                        <li
                            v-for="(result, key) in results"
                            :key="key"
                            :class="['simple-search__result', { '-is-active': isActive(key) }]"
                            role="option"
                            :aria-selected="isActive(key) ? 'true' : 'false'"
                            :id="isActive(key) ? `simpleSearchSelected-${state.id}` : ''"
                        >
                            <a :href="`/${result.url}`" :ref="`result-${key}`" :aria-label="result.title" @click="handleClick(result, $event)">
                                <div class="simple-search__result-link" v-html="result.formatted" />
                                <div class="simple-search__sub-text" v-if="type === 'personnel' && result.specialties && result.specialties.length">
                                    <template v-for="(specialty, key) in result.specialties">
                                        <span :key="key">
                                            <template v-if="(key + 1) !== result.specialties.length">{{ specialty }}, </template>
                                            <template v-else>{{ specialty }}</template>
                                        </span>
                                    </template>
                                </div>
                            </a>
                        </li>
                    </ul>
                    <div class="simple-search__result -last" v-if="type === 'personnel'">
                        <a href="/find-a-doctor">See All Physicians ></a>
                    </div>
                </div>
            </div>
        </div>
        <span class="sr-only" :id="`simpleSearchInstructions-${state.id}`">
            When autocomplete results are available use up and down arrows to review and enter to select.
        </span>
        <span aria-live="assertive" class="sr-only" aria-atomic="true">There are {{ results.length }} results.</span>
    </form>
</template>

<script>
import $mediator from 'wee-mediator';
import debounce from 'lodash.debounce';
import KeyHelper from '../../scripts/keyhelper';
import client from '../../scripts/swiftypeClient';
import Tabbable from '../../scripts/tabbable';
import uid from '../../scripts/uid';

const formatResult = (query, result) => {
    const re = new RegExp('(' + query.split(' ').join('|') + ')', 'gi');

    return `${result.replace(re, '<b>$1</b>')}`;
}

const sectionMap = {
    services: 'service',
    findADoctor: 'personnel',
    locations: 'location',
    specialties: 'specialty'
};

export default {
    mounted() {
        this.state.id = uid();

        $mediator.on('navigation.panel.toggled', () => {
            this.close();
        });
    },
    methods: {
        close() {
            this.state.open = false;
            this.state.activeIndex = -1;
        },
        open() {
            this.state.open = true;
            this.state.activeIndex = 0;
        },
        next() {
            if (this.state.activeIndex < this.results.length - 1) {
                this.state.activeIndex += 1;

                this.$refs[`result-${this.state.activeIndex}`][0].focus();
            }
        },
        prev() {
            if (this.state.activeIndex > 0) {
                this.state.activeIndex -= 1;
                this.$refs[`result-${this.state.activeIndex}`][0].focus();
            }
        },
        isActive(key) {
            return key === this.state.activeIndex;
        },
        handleFocus() {
            if (this.results.length && this.q.length) {
                this.open();
            }
        },
        handleClick(result, e) {
            client.click({
                documentId: result.id,
                query: this.q,
                requestId: this.requestId,
            });
        },
        search: debounce(function(e) {
            if (KeyHelper.isLetter(e) && this.q.length >= 2) {
                client.search(this.q, {
                    filters: {
                        type: this.type,
                    },
                    page: {
                        size: 20
                    },
                    result_fields: {
                        title: { raw: {} },
                        url: { raw: {} },
                        specialties: { raw: {} },
                        title: { raw: {} },
                    }
                }).then(({ results, info }) => {
                    this.results = results.map(result => ({
                        title: result.getRaw('title'),
                        formatted: formatResult(this.q, result.getRaw('title')),
                        specialties: result.getRaw('specialties'),
                        url: result.getRaw('url'),
                        id: result.getRaw('id'),
                    }));

                    this.requestId = info.meta.request_id;

                    if (this.results.length) {
                        this.open();
                    }

                    this.$nextTick(() => {
                        this.state.tabbable = new Tabbable({
                            el: this.$refs.results,
                            callback: () => {
                                this.$refs.input.focus();
                            },
                        });
                    });
                });
            } else if (this.q.length === 0) {
                this.close();
                // this.results = [];
                // this.state.open = false;
            }
        }, 200),
    },
    computed: {
        action() {
            return this.urls[this.type][0];
        },
        name() {
            return this.urls[this.type][1];
        },
    },
    data() {
        return {
            q: '',
            requestId: null,
            results: [],
            // Keyed by the 'type' parameter, we can get the url and the
            // corresponding field name for searching
            urls: {
                personnel: ['/find-a-doctor', 'name'],
                service: ['/find-a-service', 'query'],
            },
            state: {
                activeIndex: -1,
                id: '',
                tabbable: null,
                open: false,
            }
        };
    }
}
</script>
